import { ChevronDownIcon } from '@heroicons/react/24/outline';
import { ArchiveBoxIcon, EnvelopeIcon, PhoneIcon, PrinterIcon } from '@heroicons/react/24/solid';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import clsx from 'clsx';
import { useCallback, useMemo, useRef, useState } from 'react';
import { useDispatch } from 'react-redux';
import { useParams } from 'react-router';
import { useReactToPrint } from 'react-to-print';
import { type GetCommentParams, getPartnerProposalComments, postComment } from '@/api/comments';
import { getApplication, patchArchiveApplication } from '@/api/Rover/ApplicationForm';
import { readApiError, roverNotificationsApi } from '@/api/Rover/notifications/roverNotifications';
import type { StatusChangeContext } from '@/api/Rover/notifications/roverNotifications.types';
import Comments from '@/components/Comments';
import { HasPermission } from '@/components/Permission/HasPermission';
import ApplicationTimeline from '@/components/Rover/DeveloperApplications/ApplicationTimeline';
import Card from '@/components/UI/Card/index.tsx';
import DropDown, { type DropDownMenuItem } from '@/components/UI/DropDown';
import ErrorCard from '@/components/UI/ErrorCard';
import LoadingTdmDetail from '@/components/UI/loading/LoadingTdmDetail';
import Confirm from '@/components/UI/Modal/Confirm';
import { PhoneNumberFormatDisplay } from '@/components/UI/NumberFormatDisplay/PhoneNumberFormatDisplay';
import { RoverReactQueryKey } from '@/constants/ReactQueryKeys';
import { applicationStatusMapping, EApplicationStatus } from '@/features/ApplicationForm/constants';
import { formConfigurationMapping } from '@/features/ApplicationForm/FormConfig';
import { notificationHighlightStorageKey } from '@/features/ApplicationForm/utils/notifications/statusCatalog';
import { useApplicationForm } from '@/hooks/ReactQueryHooks/useApplicationFromQuery';
import { useTitleByName } from '@/hooks/ReactQueryHooks/useTitlesQuery';
import { useUserInvite } from '@/hooks/ReactQueryHooks/useUsersQuery';
import { usePermission } from '@/hooks/usePermission';
import { notifyUser } from '@/store/Toast';
import { convertEnumToText } from '@/utils';
import ApplicationDetailsNav from '../ApplicationDetailsNav';
import Styles from './ApplicationDetails.module.css';

export const ApplicationDetails = () => {
  const { applicationId, applicationFormId } = useParams<string>();
  const routeApplicationId = applicationId ?? '';
  const routeApplicationFormId = applicationFormId ?? '';
  const queryClient = useQueryClient();

  const dispatch = useDispatch();
  const userInvite = useUserInvite({});
  const orgTitles = useTitleByName('House of Worship');

  const contentRef = useRef<HTMLDivElement>(null);
  const reactToPrintFn = useReactToPrint({ contentRef });

  const { hasPermission } = usePermission();
  const [showArchiveConfirm, setShowArchiveConfirm] = useState(false);
  const [printMode, setPrintMode] = useState(false);

  const fetchApplicationForm = useApplicationForm({
    formId: routeApplicationFormId,
  });
  const applicationForm = fetchApplicationForm?.data?.data;

  //Tanstack: version 5 query implementation
  const fetchApplicationDetails = useQuery({
    queryKey: [RoverReactQueryKey.GET_APPLICATION_DETAILS, routeApplicationId],
    queryFn: () => getApplication({ applicationId: routeApplicationId }),
    enabled: Boolean(routeApplicationId && routeApplicationFormId),
  });
  const applicationDetails = fetchApplicationDetails?.data?.data;

  const postComments = useCallback(
    async (values: { comment: string }) => {
      const response = await postComment({
        data: {
          resource_id: routeApplicationId,
          resource_type: 'Application',
          ...values,
        },
      });
      return response;
    },
    [routeApplicationId]
  );

  const getComments = useCallback(
    (_params: GetCommentParams) => {
      return getPartnerProposalComments({
        params: {
          resource_id: routeApplicationId,
          resource_type: 'Application',
        },
      });
    },
    [routeApplicationId]
  );

  const [optionPrompt, setOptionPrompt] = useState<{
    status: EApplicationStatus;
    context: StatusChangeContext;
    selectedIds: string[];
  } | null>(null);

  const reportStatusError = useCallback(
    (error: unknown) => {
      dispatch(
        notifyUser({
          showToast: true,
          toastMessage: readApiError(error).message,
          toastType: 'failed',
        })
      );
    },
    [dispatch]
  );

  const changeApplicationStatus = useMutation(
    (data: { status: EApplicationStatus; selectedOptionIds?: string[] }) => roverNotificationsApi.changeStatus(routeApplicationId, data),
    {
      onSuccess: (result, payload) => {
        if (routeApplicationFormId) {
          sessionStorage.setItem(notificationHighlightStorageKey(routeApplicationFormId), payload.status);
        }
        const channelSummary = result.notifications
          .map((n) => (n.state === 'queued' ? `${n.channel} queued` : `${n.channel} skipped (${n.reason})`))
          .join(', ');
        dispatch(
          notifyUser({
            showToast: true,
            toastMessage: `Submission status updated. ${channelSummary}`,
            toastType: 'success',
          })
        );

        queryClient.invalidateQueries({ queryKey: [RoverReactQueryKey.GET_APPLICATION_DETAILS] });
        queryClient.invalidateQueries({ queryKey: [RoverReactQueryKey.GET_APPLICATIONS] });
        queryClient.invalidateQueries({ queryKey: ['application-audits', routeApplicationId] });

        if (
          payload.status === 'APPROVED' &&
          applicationForm.templateCode === '0572bb50' &&
          applicationDetails.contactName &&
          applicationDetails.contactEmail &&
          orgTitles.matchedTitle
        ) {
          userInvite.mutate({
            name: applicationDetails.contactName,
            email: applicationDetails.contactEmail,
            titleId: orgTitles.matchedTitle.id,
          });
        }
      },
      onError: (error) => reportStatusError(error),
    }
  );

  const statusChangeHandler = useCallback(
    async (status: EApplicationStatus) => {
      try {
        const context = await roverNotificationsApi.getStatusChangeContext(routeApplicationId, status);
        if (context.requiresOptions) {
          setOptionPrompt({ status, context, selectedIds: [] });
          return;
        }
        changeApplicationStatus.mutate({ status });
      } catch (error) {
        reportStatusError(error);
      }
    },
    [changeApplicationStatus, reportStatusError, routeApplicationId]
  );

  let statusChangeList: DropDownMenuItem[] = useMemo(
    () => [
      {
        name: 'Initial Vetting',
        clicked: () => statusChangeHandler(EApplicationStatus.INITIAL_VETTING),
      },
      {
        name: 'Document Review',
        clicked: () => statusChangeHandler(EApplicationStatus.DOCUMENT_REVIEW),
      },
      {
        name: 'Documents Requested',
        clicked: () => statusChangeHandler(EApplicationStatus.DOCUMENT_REQUESTED),
      },
      {
        name: 'Awaiting 3rd Party Report',
        clicked: () => statusChangeHandler(EApplicationStatus.AWAITING_3RD_PARTY_REPORT),
      },
      {
        name: 'Financial Underwriting',
        clicked: () => statusChangeHandler(EApplicationStatus.FINANCIAL_UNDERWRITING),
      },
      {
        name: 'Project Underwriting',
        clicked: () => statusChangeHandler(EApplicationStatus.PROJECT_UNDERWRITING),
      },
      {
        name: 'Partner Underwriting',
        clicked: () => statusChangeHandler(EApplicationStatus.PARTNER_UNDERWRITING),
      },
      {
        name: 'Physical Inspections',
        clicked: () => statusChangeHandler(EApplicationStatus.PHYSICAL_INSPECTION),
      },
      {
        name: 'Applicant Underwriting',
        clicked: () => statusChangeHandler(EApplicationStatus.APPLICANT_UNDERWRITING),
      },
      {
        name: 'Reference Checking',
        clicked: () => statusChangeHandler(EApplicationStatus.REFERENCE_CHECKING),
      },
      {
        name: 'Internal Review',
        clicked: () => statusChangeHandler(EApplicationStatus.INTERNAL_REVIEW),
      },
      {
        name: 'Under Review',
        clicked: () => statusChangeHandler(EApplicationStatus.UNDER_REVIEW),
      },
      {
        name: 'Deny',
        clicked: () => statusChangeHandler(EApplicationStatus.DENIED),
      },
      {
        name: 'Approve',
        clicked: () => statusChangeHandler(EApplicationStatus.APPROVED),
      },
    ],
    [statusChangeHandler]
  );

  if (applicationDetails?.status !== 'SUBMITTED') {
    statusChangeList = statusChangeList.filter((s) => applicationStatusMapping[s.name.toUpperCase()] !== applicationDetails?.status);
  }

  const archiveApplication = useMutation(() => patchArchiveApplication({ applicationId: routeApplicationId }), {
    onSuccess: () => {
      dispatch(
        notifyUser({
          showToast: true,
          toastMessage: 'Submission archive status updated successfully.',
          toastType: 'success',
        })
      );
      queryClient.invalidateQueries([RoverReactQueryKey.GET_APPLICATION_DETAILS]);
    },
    onError: () => {
      dispatch(
        notifyUser({
          showToast: true,
          toastMessage: 'Submission archive status update failed.',
          toastType: 'failed',
        })
      );
    },
  });

  const confirmArchiveApplication = async () => {
    archiveApplication.mutate();
    setShowArchiveConfirm(false);
  };

  const cancelArchiveApplication = () => {
    setShowArchiveConfirm(false);
  };

  if (fetchApplicationForm.isLoading || fetchApplicationDetails.isLoading) {
    return <LoadingTdmDetail />;
  }

  if (fetchApplicationDetails.isError) {
    return (
      <>
        <ApplicationDetailsNav />
        <div className='my-5 h-screen/2 flex items-center'>
          <ErrorCard
            errorTitle='Submission Unavailable'
            errorMsg='You do not have permission to view this submission, or the submission could not be loaded.'
          />
        </div>
      </>
    );
  }

  const applicationFromConfig = formConfigurationMapping.find((fcm) => fcm.formCode === applicationForm?.templateCode);

  if (!applicationFromConfig || !('applicationDetailsComponent' in applicationFromConfig)) {
    return <h2>Submission Detail component not found</h2>;
  }
  const ApplicationDetailsComponent = applicationFromConfig.applicationDetailsComponent;

  if (!applicationDetails) {
    return (
      <>
        <ApplicationDetailsNav />
        <div className='my-5 h-screen/2 flex items-center'>
          <ErrorCard errorTitle='Submission Not Found' errorMsg='We could not find details for this submission.' />
        </div>
      </>
    );
  }

  const handlePrint = () => {
    setPrintMode(true);
    setTimeout(() => {
      reactToPrintFn();
      setPrintMode(false);
    }, 1000);
  };

  return (
    <>
      <ApplicationDetailsNav />

      <div className='min-h-full max-w-screen-2xl mx-auto'>
        {applicationDetails && (
          <main>
            {/* Page header */}
            <div className='w-full mx-auto px-4 sm:px-6 flex justify-between items-center md:justify-end md:space-x-5 lg:px-8'>
              <div className='flex flex-col-reverse justify-stretch space-y-4 space-y-reverse sm:flex-row-reverse sm:justify-end sm:space-x-reverse sm:space-y-0 sm:space-x-3 md:mt-0 md:flex-row md:space-x-3'>
                {hasPermission(['UPDATE_APPLICATION']) && (
                  <DropDown menuItems={statusChangeList}>
                    <div className={`${Styles[applicationDetails.status]}`}>
                      <span className='font-semibold capitalize'>{convertEnumToText(applicationDetails.status)}</span>
                      <ChevronDownIcon className='h-4 w-4' />
                    </div>
                  </DropDown>
                )}
                {!hasPermission(['UPDATE_APPLICATION']) && (
                  <div className={`${Styles[applicationDetails.status]}`}>
                    <span className='font-semibold capitalize'>{convertEnumToText(applicationDetails.status)}</span>
                    <ChevronDownIcon className='h-4 w-4' />
                  </div>
                )}
              </div>
              <HasPermission of='ARCHIVE_APPLICATION'>
                <button
                  className='flex gap-1 rounded-full bg-neutralColor-lighter px-4 py-2 text-neutralColor-normal font-semibold hover:bg-neutralColor-light cursor-pointer'
                  onClick={() => setShowArchiveConfirm(true)}
                >
                  <ArchiveBoxIcon className='h-5 w-5' />
                  <div className='hidden md:block'>{applicationDetails?.archived ? 'Unarchive' : 'Archive'}</div>
                </button>
              </HasPermission>
              <button
                className='flex gap-1 rounded-full bg-neutralColor-lighter px-4 py-2 text-neutralColor-normal font-semibold hover:bg-neutralColor-light cursor-pointer'
                onClick={handlePrint}
              >
                <PrinterIcon className='h-5 w-5' />
                <div className='hidden md:block'>Print Submission</div>
              </button>
            </div>

            <div ref={contentRef} className='sm:px-6 mt-8 w-full mx-auto grid grid-cols-12 gap-6 '>
              {applicationDetails && (
                <div className={clsx('space-y-6 col-span-12', !printMode && 'xl:col-span-8')}>
                  {/* Description list*/}
                  <ApplicationDetailsComponent
                    applicationData={applicationDetails.applicationData}
                    files={applicationDetails?.files}
                    canUploadFiles={!applicationDetails.archived}
                  />
                  <Comments getComments={getComments} postComments={postComments} queryKey='application-comments' />
                </div>
              )}
              <div className={clsx('col-span-12 h-screen/2 space-y-4', !printMode && 'xl:col-span-4')}>
                <div className='max-h-full'>
                  <Card title='Applicant Information'>
                    <dl className='p-4 space-y-4'>
                      <div className='flex justify-between'>
                        <dt>Applicant Name</dt>
                        <dd>{applicationDetails.applicationData?.applicantInfo?.fullName || applicationDetails.contactName}</dd>
                      </div>
                      <div className='flex justify-between'>
                        <dt>Contact Email</dt>
                        <dd className='flex gap-2'>
                          <span>
                            <EnvelopeIcon className='h-5 w-5' />
                          </span>
                          <span>
                            {applicationDetails.applicationData?.applicantInfo?.contactEmail || applicationDetails.contactEmail || 'None'}
                          </span>
                        </dd>
                      </div>
                      <div className='flex justify-between'>
                        <dt>Contact Phone</dt>
                        <dd className='flex gap-2'>
                          <span>
                            <PhoneIcon className='h-5 w-5' />
                          </span>
                          <span>
                            {applicationDetails.applicationData?.applicantInfo?.contactPhone ? (
                              <PhoneNumberFormatDisplay value={applicationDetails.applicationData?.applicantInfo?.contactPhone} />
                            ) : (
                              'None'
                            )}
                          </span>
                        </dd>
                      </div>
                    </dl>
                  </Card>
                </div>

                <ApplicationTimeline activity={applicationDetails} resourceType='Application' />
              </div>
            </div>
          </main>
        )}
      </div>

      {showArchiveConfirm && (
        <Confirm
          title={applicationDetails?.archived ? 'Unarchive Submission' : 'Archive Submission'}
          message={
            applicationDetails?.archived
              ? 'You are about to unarchive the submission. Are you sure you want to unarchive ?'
              : 'You are about to archive the submission. Are you sure you want to archive ?'
          }
          acceptButtonLabel={applicationDetails?.archived ? 'Unarchive' : 'Archive'}
          declineButtonLabel='Cancel'
          accept={confirmArchiveApplication}
          decline={cancelArchiveApplication}
        />
      )}
      {optionPrompt && (
        <Confirm
          title={`Move to ${optionPrompt.context.statusLabel}`}
          message='Choose the message options applicants will receive with this status change.'
          acceptButtonLabel='Change status'
          declineButtonLabel='Cancel'
          decline={() => setOptionPrompt(null)}
          accept={async () => {
            const { status, selectedIds, context } = optionPrompt;
            if (context.optionsSelectionMode === 'single' ? selectedIds.length !== 1 : selectedIds.length < 1) {
              dispatch(
                notifyUser({
                  showToast: true,
                  toastMessage: context.optionsSelectionMode === 'single' ? 'Select exactly one option.' : 'Select at least one option.',
                  toastType: 'failed',
                })
              );
              return;
            }
            try {
              await changeApplicationStatus.mutateAsync({ status, selectedOptionIds: selectedIds });
              setOptionPrompt(null);
            } catch {
              // The mutation reports the API error.
            }
          }}
          content={
            <fieldset className='mt-4 flex flex-col gap-2 text-sm'>
              <legend className='mb-2 font-semibold'>
                {optionPrompt.context.optionsSelectionMode === 'single' ? 'Select one option' : 'Select one or more options'}
              </legend>
              {optionPrompt.context.options.map((option) => {
                const checked = optionPrompt.selectedIds.includes(option.id);
                return (
                  <label key={option.id} className='flex items-start gap-2'>
                    <input
                      type={optionPrompt.context.optionsSelectionMode === 'single' ? 'radio' : 'checkbox'}
                      name='status-change-option'
                      checked={checked}
                      onChange={() => {
                        setOptionPrompt((current) => {
                          if (!current) return current;
                          const selectedIds =
                            current.context.optionsSelectionMode === 'single'
                              ? [option.id]
                              : checked
                                ? current.selectedIds.filter((id) => id !== option.id)
                                : [...current.selectedIds, option.id];
                          return { ...current, selectedIds };
                        });
                      }}
                    />
                    <span>
                      <span className='font-semibold'>{option.label}</span>
                      {option.messageText ? <span className='block text-neutralColor-normal'>{option.messageText}</span> : null}
                    </span>
                  </label>
                );
              })}
            </fieldset>
          }
        />
      )}
    </>
  );
};
